/**
 * Global axios interceptor that redirects to /login on 401 responses
 * and pipes a session-expired hint through as a query param the login
 * page can surface.
 *
 * Backdrop: the whole app was calling `axios` (the global instance) —
 * there was no per-component 401 handling except NewRequest.vue which
 * had a bespoke redirect. So a stale token on any other screen would
 * either render a raw error, silently blank a table, or worst-case
 * loop on a background poll while the customer stared at empty state.
 *
 * A single response interceptor here handles it once. Reasons:
 * - `axios` singleton — everyone uses it, so one attach point covers
 *   everything.
 * - Distinguishes token_expired (session ended, benign) from
 *   invalid_token (tampered / signature mismatch, still just log-out
 *   from the customer's POV but we suppress the "session expired"
 *   banner because the message would be misleading).
 * - Public endpoints (Login, ForgotPassword, VerifyOTP, Signup, and the
 *   anonymous /trip page) MUST NOT redirect on 401 — a wrong password
 *   or bad OTP returns 401 too, and hijacking those to /login would
 *   destroy the error message the user is trying to read. We opt those
 *   requests out by URL prefix.
 *
 * Import once from main.ts — the side-effect installs the interceptor
 * on module load. Idempotent (installedFlag prevents double-attach
 * during HMR).
 */

import axios from 'axios'

const AUTH_PUBLIC_PREFIXES = [
  // Anything the login/signup flow itself hits — a 401 here means "bad
  // credentials", not "you got logged out". Redirecting would erase
  // the message the form is about to render.
  '/login',
  '/signup',
  '/verify_otp',
  '/verify-otp',
  '/forgot_password',
  '/forgot-password',
  '/reset_password',
  '/reset-password',
  '/otp',
  // Public tracking page — anonymous callers can validly hit 401 if
  // they mistype a token; we render the page's own error state.
  '/trip',
]

function isPublicAuthRequest(url: string | undefined): boolean {
  if (!url) return false
  try {
    // Strip origin to compare against the path portion; axios calls
    // that use a full URL (VITE_* base + path) still match.
    const path = url.startsWith('http') ? new URL(url).pathname : url
    return AUTH_PUBLIC_PREFIXES.some((p) => path.includes(p))
  } catch {
    // Non-URL string — best-effort substring match.
    return AUTH_PUBLIC_PREFIXES.some((p) => url.includes(p))
  }
}

let installed = false

export function installHttpAuthInterceptor(): void {
  if (installed) return
  installed = true

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status
      const url = error?.config?.url
      if (status !== 401) return Promise.reject(error)
      if (isPublicAuthRequest(url)) return Promise.reject(error)

      // We're on a private page and the token stopped working. Clear
      // whatever the app has stashed, then bounce to /login with a
      // reason chip so the login page can surface a friendlier
      // "Session expired" banner (vs "Please sign in").
      const body = error?.response?.data
      const code = body?.code || (typeof body === 'object' ? body?.reason : undefined)
      const reason = code === 'token_expired' ? 'expired' : 'unauth'

      try {
        // The customer app stashes its JWT under _panda (set by
        // Login.vue and read by NewRequest.vue / BusinessProfile.vue /
        // ResetPassword.vue / etc). The previous 'token' key was a
        // typo — the dead JWT persisted, so any component that ran
        // `axios.defaults.headers.common['Authorization'] = 'Bearer '
        // + localStorage.getItem('_panda')` on mount would immediately
        // re-attach the expired token and loop-401 the user forever.
        localStorage.removeItem('_panda')
        // The default Authorization header would keep replaying the
        // dead token — clear it so the next login's fresh token sticks.
        delete axios.defaults.headers.common['Authorization']
      } catch {
        // localStorage inaccessible (SSR / storage disabled) — nothing
        // to clean up.
      }

      // Skip if we're already on /login to avoid a redirect loop when
      // login-page code accidentally hits an authed endpoint.
      const path = window.location.pathname || ''
      if (!path.startsWith('/login')) {
        const target = `/login?reason=${reason}&next=${encodeURIComponent(path + window.location.search)}`
        window.location.assign(target)
      }
      return Promise.reject(error)
    },
  )
}
